import React, { useState } from "react";
import axios from "axios";
import { useI18n } from "../../lib/i18n";
import { toast } from "sonner";
import { PaperPlaneTilt, Phone, EnvelopeSimple, MapPin, Clock } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t } = useI18n();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact.success);
      setForm({ name: "", email: "", phone: "", service: "", subject: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error(t.contact.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-[#0A1428] text-white py-24 lg:py-32 relative overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "url(https://static.prod-images.emergentagent.com/jobs/90dd8daa-cd26-4f72-b7b0-ddd9192df92a/images/637ebb2bbeed047f419e8449b463145a4df9d3b751e4a9fca8c9757ba1af6b9c.png)",
          backgroundSize: "cover",
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-14 lg:gap-20">
          {/* Info side */}
          <div className="lg:col-span-5">
            <p className="overline mb-5">{t.contact.overline}</p>
            <h2 className="font-serif text-4xl lg:text-5xl xl:text-6xl leading-[1.1] tracking-tight font-medium">
              {t.contact.title}
            </h2>
            <p className="mt-6 text-white/70 text-lg leading-relaxed">{t.contact.subtitle}</p>

            <div className="mt-12 space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 border border-[#C5A059]/40 flex items-center justify-center flex-shrink-0">
                  <MapPin size={18} className="text-[#C5A059]" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-1">{t.contact.office}</p>
                  <p className="text-white">Piazza della Repubblica, 5<br />10034 Chivasso (TO)</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 border border-[#C5A059]/40 flex items-center justify-center flex-shrink-0">
                  <Phone size={18} className="text-[#C5A059]" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-1">{t.contact.phoneLabel}</p>
                  <a href="tel:+393935902294" className="text-white hover:text-[#C5A059]">+39 393 590 2294</a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 border border-[#C5A059]/40 flex items-center justify-center flex-shrink-0">
                  <EnvelopeSimple size={18} className="text-[#C5A059]" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-1">{t.contact.emailLabel}</p>
                  <a href="mailto:makelaars360@gmail.com" className="text-white hover:text-[#C5A059]">makelaars360@gmail.com</a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 border border-[#C5A059]/40 flex items-center justify-center flex-shrink-0">
                  <Clock size={18} className="text-[#C5A059]" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-1">{t.contact.hours}</p>
                  <p className="text-white">{t.contact.hoursValue}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="bg-white text-[#0A1428] p-8 lg:p-12"
            >
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#4A5568] font-semibold">
                    {t.contact.name} *
                  </label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={update("name")}
                    className="input-elegant mt-2"
                    data-testid="contact-input-name"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#4A5568] font-semibold">
                    {t.contact.email} *
                  </label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    className="input-elegant mt-2"
                    data-testid="contact-input-email"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#4A5568] font-semibold">
                    {t.contact.phone}
                  </label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    className="input-elegant mt-2"
                    data-testid="contact-input-phone"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#4A5568] font-semibold">
                    {t.contact.service}
                  </label>
                  <select
                    value={form.service}
                    onChange={update("service")}
                    className="input-elegant mt-2 bg-transparent"
                    data-testid="contact-select-service"
                  >
                    <option value="">{t.contact.selectService}</option>
                    <option value="wealth">{t.services.items.wealth.title}</option>
                    <option value="sale">{t.services.items.sale.title}</option>
                    <option value="invest">{t.services.items.invest.title}</option>
                    <option value="auctions">{t.services.items.auctions.title}</option>
                    <option value="consulting">{t.services.items.consulting.title}</option>
                    <option value="enhancement">{t.services.items.enhancement.title}</option>
                    <option value="valorization">{t.services.items.valorization.title}</option>
                    <option value="insurance">{t.services.items.insurance.title}</option>
                    <option value="interior">{t.services.items.interior.title}</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs uppercase tracking-widest text-[#4A5568] font-semibold">
                    {t.contact.subject}
                  </label>
                  <input
                    type="text"
                    value={form.subject}
                    onChange={update("subject")}
                    className="input-elegant mt-2"
                    data-testid="contact-input-subject"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs uppercase tracking-widest text-[#4A5568] font-semibold">
                    {t.contact.message} *
                  </label>
                  <textarea
                    required
                    value={form.message}
                    onChange={update("message")}
                    rows={5}
                    className="input-elegant mt-2 resize-none"
                    data-testid="contact-input-message"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-button"
                className="btn-primary mt-8 disabled:opacity-50"
              >
                {submitting ? t.contact.sending : t.contact.submit}
                <PaperPlaneTilt size={16} weight="bold" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
